import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Input, Pagination, Table } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import {
  ClickWrapper,
  FlexWrapper,
  HighlightText,
  PaginationBox,
  ViewIconBox
} from '../../theme/common_style';
import EmptyData from '../../components/common/EmptyData';
import { getAdminGroupedReportings } from '../../services/api_collection';
import {
  checkPermission,
  debounce,
  generateEmployeeImgUrl,
  getFullName
} from '../../utils/common_functions';
import ReportingFilters from './ReportingFilters';
import TableLoader from '../../components/loaders/TableLoader';
import { StickyBox } from '../../utils/style';
import { AvatarGroup } from '../../components/common/AvatarGroup';
import {
  FilterIconNew,
  ReportNotFoundIcon,
  SearchIconNew,
  ViewIconNew
} from '../../theme/SvgIcons';
import { actionTypeEnums, apiModules } from '../../utils/constant';
import { useTheme } from '@emotion/react';
import ViewReporting from './ViewReporting';
import CreateManualReport from './CreateManualReport';
import styled from '@emotion/styled';

const Reporting = () => {
  const { isEmployee } = useSelector((e) => e.userInfo);
  const [appliedFilter, setAppliedFilter] = useState({});
  const { permissions } = useSelector((state) => state?.userInfo?.data);
  const [filtersModal, setFiltersModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(null);
  const [total, setTotal] = useState(0);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const navigate = useNavigate();
  const theme = useTheme();

  const { CREATE, READ } = actionTypeEnums;
  const { REPORTING, EMPLOYEE } = apiModules;
  const canCreate = checkPermission(REPORTING, CREATE, permissions);
  const canReadEmp = checkPermission(EMPLOYEE, READ, permissions);

  const [createModal, setCreateModal] = useState(false);

  const handleGetList = async (search) => {
    try {
      setLoading(true);
      let params = new URLSearchParams();
      params.append('limit', limit);
      params.append('page', page);
      search && params.append('search', search);
      Object?.entries(appliedFilter || {}).forEach(([key, value]) => {
        if (value) {
          params.append(key, value?.toString());
        }
      });
      const res = await getAdminGroupedReportings(params);
      if (res?.statusCode === 200) {
        let array = (res?.data?.employees || [])?.map((el, idx) => ({
          ...el,
          key: idx + 1
        }));
        setData(array || []);
        setTotal(res?.data?.pagination?.total);
      } else toast.error(res?.message || 'Something went wrong');
    } catch (err) {
      toast.error(err?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const optimizedFn = useCallback(debounce(handleGetList), [appliedFilter, page]);

  const columns = [
    {
      title: 'S.NO',
      dataIndex: 'key',
      key: 'key',
      render: (text) => <span>{(page - 1) * limit + text}.</span>
    },
    {
      title: 'Emp Name',
      dataIndex: 'first_name',
      key: 'first_name',
      render: (_, record) => {
        const name = getFullName(record?.first_name, record?.middle_name, record?.last_name);
        const empData = [
          {
            name: name,
            src: generateEmployeeImgUrl(record?.id),
            id: record?.id
          }
        ];
        return (
          <ClickWrapper
            onClick={() =>
              canReadEmp &&
              navigate(`/view-employee`, {
                state: {
                  id: record?.id,
                  name: name
                }
              })
            }>
            <FlexWrapper
              justify="start"
              cursor={canReadEmp ? 'pointer' : 'unset'}
              gap="6px"
              wrap="nowrap">
              <AvatarGroup avatars={empData} />
              <HighlightText>{name || 'N/A'}</HighlightText>
            </FlexWrapper>
          </ClickWrapper>
        );
      }
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      render: (department) => <span>{department?.name || 'N/A'}</span>
    },
    {
      title: 'Date',
      dataIndex: 'latest_reporting',
      key: 'latest_reporting',
      render: (latest_reporting) =>
        latest_reporting?.reporting_date
          ? dayjs(latest_reporting?.reporting_date).format('DD-MM-YYYY')
          : 'N/A'
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      className: 'action-column',
      render: (_, record) => {
        return (
          <ClickWrapper
            onClick={() =>
              navigate(`/view-report`, {
                state: {
                  id: record?.id,
                  reportName: getFullName(
                    record?.first_name,
                    record?.middle_name,
                    record?.last_name
                  ),
                  startDate: dayjs(record?.latest_reporting?.reporting_date)
                    .subtract(6, 'day')
                    .format('YYYY-MM-DD'),
                  endDate: dayjs(record?.latest_reporting?.reporting_date).format('YYYY-MM-DD')
                }
              })
            }>
            <ViewIconBox style={{ cursor: 'pointer' }}>
              <ViewIconNew />
            </ViewIconBox>
          </ClickWrapper>
        );
      }
    }
  ];

  const handlePageChange = (page) => {
    setPage(page);
  };

  useEffect(() => {
    if (search !== null) {
      !isEmployee && optimizedFn(search);
    } else {
      !isEmployee && handleGetList();
    }
  }, [page, search, appliedFilter]);

  return (
    <div>
      {!isEmployee && createModal && (
        <CreateManualReport open={createModal} onCancel={() => setCreateModal(false)} />
      )}
      {filtersModal && (
        <ReportingFilters
          setAppliedFilter={(val) => {
            setAppliedFilter(val);
            setPage(1);
          }}
          appliedFilter={appliedFilter}
          open={filtersModal}
          onClose={() => setFiltersModal(false)}
        />
      )}
      {!isEmployee && (
        <StickyBox padding="8px">
          <FlexWrapper
            width={'100%'}
            justify={canCreate ? 'space-between' : 'flex-end'}
            gap="16px"
            cursor="default"
            style={{ marginBottom: '16px' }}>
            {!isEmployee && canCreate && (
              <Button type="text" prefixCls="antCustomBtn" onClick={() => setCreateModal(true)}>
                Add Manual Report
              </Button>
            )}
            {!isEmployee && (
              <FlexWrapperBox justify={'center'} gap="6px" cursor="default">
                <Input
                  allowClear
                  prefixCls="antCustomInput"
                  placeholder="Search by Employee Name..."
                  prefix={<SearchIconNew />}
                  className="searchInput"
                  onChange={(e) => {
                    setSearch(e?.target?.value.trim());
                    setPage(1);
                  }}
                />
                <Badge
                  count={Object?.values(appliedFilter || {})?.reduce(
                    (acc, item) =>
                      acc +
                      (item
                        ? Array.isArray(item)
                          ? item?.length > 0 && item?.[0] !== null
                            ? 1
                            : 0
                          : 1
                        : 0),
                    0
                  )}
                  color={theme.primaryColor}
                  offset={[-9, 4]}
                  prefixCls="filterBadge">
                  <Button prefixCls="custom-filter-btn" onClick={() => setFiltersModal(true)}>
                    <FilterIconNew /> Filter
                  </Button>
                </Badge>
              </FlexWrapperBox>
            )}
          </FlexWrapper>
        </StickyBox>
      )}

      {!isEmployee && (
        <>
          {data.length > 0 || loading ? (
            <Table
              prefixCls="antCustomTable"
              dataSource={data}
              columns={columns}
              pagination={false}
              loading={{ spinning: loading, indicator: TableLoader }}
            />
          ) : (
            <EmptyData
              height={'70vh'}
              icon={<ReportNotFoundIcon />}
              title={'No Report'}
              subTitle={'No Report Found'}
            />
          )}
          {total > limit && (
            <PaginationBox>
              <Pagination
                current={page}
                prefixCls="custom-pagination"
                pageSize={limit}
                total={total}
                onChange={handlePageChange}
                showSizeChanger={false}
              />
            </PaginationBox>
          )}
        </>
      )}

      {isEmployee && <ViewReporting />}
    </div>
  );
};

export default Reporting;
const FlexWrapperBox = styled.div`
  width: ${(props) => props.width};
  display: flex;
  flex-direction: ${(props) => props.direction || 'row'};
  justify-content: ${(props) => props.justify || 'center'};
  align-items: ${(props) => props.align || 'center'};
  gap: ${(props) => props.gap || '10px'};
  cursor: ${(props) => props.cursor || 'auto'};
  margin: ${(props) => props.margin || '0px'};

  .searchInput {
    width: 250px;
  }
  @media (max-width: 668px) {
    width: 100%;
    .searchInput {
      width: 100%;
    }
    display: grid;
    grid-template-columns: 70% 30%;
    gap: 5px;
  }
`;
